// landing/sections/VerdictsSection.tsx — what a Council decision actually
// reads like, on the page that claims decisions are public.
//
// The two cards are CURATED examples written to be representative of real
// Council output (owner ruling, 1.24.70 spec): one keep, one remove, each
// with the written reasons that make the system what it is. They are labelled
// as examples — the live record is one click away at /council, and that link
// is the honest half of this section: don't take the samples' word for it.

import { Link } from 'react-router-dom';
import {
  OUTCOME_LABEL,
  VERDICT_EXAMPLES,
  VERDICTS_HEADING,
  VERDICTS_INTRO,
  VERDICTS_RECEIPTS_AFTER,
  VERDICTS_RECEIPTS_BEFORE,
  VERDICTS_RECEIPTS_LINK,
} from './verdictsCopy';
import styled from 'styled-components';
import { ConsolePanel } from '@/ui';
import { Icon } from '@/icons';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Intro = styled.p`
  margin: 0;
  max-width: 62ch;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Cards = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
`;

const Card = styled(ConsolePanel).attrs({ as: 'li' })`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Outcome = styled.span<{ $removed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme, $removed }) => ($removed ? theme.color.danger : theme.color.success)};
  border: 1px solid currentColor;
`;

const CaseMeta = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textFaint};
`;

const Reasons = styled.blockquote`
  margin: 0;
  padding-left: 12px;
  border-left: 2px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Receipts = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};

  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

export function VerdictsSection() {
  return (
    <Wrap aria-labelledby="landing-verdicts-heading">
      <SectionHeading id="landing-verdicts-heading">{VERDICTS_HEADING}</SectionHeading>
      <Intro>{VERDICTS_INTRO}</Intro>
      <Cards>
        {VERDICT_EXAMPLES.map((v) => (
          <Card key={v.category}>
            <CardHead>
              <Outcome $removed={v.outcome === 'removed'}>
                <Icon name={v.outcome === 'removed' ? 'ban' : 'check'} size={12} />
                {OUTCOME_LABEL[v.outcome]}
              </Outcome>
              <CaseMeta>{v.category}</CaseMeta>
            </CardHead>
            <Reasons>{v.reasons}</Reasons>
          </Card>
        ))}
      </Cards>
      <Receipts>
        {VERDICTS_RECEIPTS_BEFORE}
        <Link to="/council">{VERDICTS_RECEIPTS_LINK}</Link>
        {VERDICTS_RECEIPTS_AFTER}
      </Receipts>
    </Wrap>
  );
}
